import QRCode from 'qrcode';
import dayjs from 'dayjs';
import vmSymbolUrl from '../assets/Icon_svg_nopadding.svg';
import { marketingLogos, marketingPalette } from '../content/marketing';

// A4 portrait at 300 DPI, so the PNG and the PDF print sharply.
export const POSTER_WIDTH = 2480;
export const POSTER_HEIGHT = 3508;

const HEADING_FONT = '"Sora", "Manrope", "Segoe UI", sans-serif';
const BODY_FONT = '"Manrope", "Segoe UI", sans-serif';
const QR_INK = marketingPalette.heroBlueDeeper;
const FINDER_SIZE = 7;

// Vertical layout (in poster pixels).
const MARGIN_X = 170;
const HERO_HEIGHT = 1480;
const HEADLINE_TOP = 790;
const CARD_SIZE = 1380;
const CARD_TOP = 1200;
const FOOTER_HEIGHT = 210;

const palette = {
  deep: marketingPalette.heroBlueDeeper,
  mid: marketingPalette.heroBlueMid,
  bright: marketingPalette.heroBlue,
  sky: '#5dcbf1',
  mist: marketingPalette.heroBlueMist,
  ink: marketingPalette.ink,
  inkSoft: marketingPalette.inkSoft,
};

const relativeLuminance = (hex) => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!match) {
    return null;
  }
  const value = parseInt(match[1], 16);
  const channels = [(value >> 16) & 255, (value >> 8) & 255, value & 255].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};

// Only use a brand colour for the QR finder "eyes" when it stays dark enough
// against white for phone cameras to read reliably.
export const isScannableAccent = (hex) => {
  const luminance = relativeLuminance(hex);
  if (luminance === null) {
    return false;
  }
  return (1.05 / (luminance + 0.05)) >= 4.5;
};

export const displayJoinUrl = (url = '') => url.replace(/^https?:\/\//i, '');

const roundRectPath = (ctx, x, y, width, height, radius) => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const fillRoundRect = (ctx, x, y, width, height, radius, fillStyle) => {
  roundRectPath(ctx, x, y, width, height, radius);
  ctx.fillStyle = fillStyle;
  ctx.fill();
};

const wrapText = (ctx, text, maxWidth, maxLines) => {
  const words = (text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  });
  if (current) {
    lines.push(current);
  }

  if (lines.length <= maxLines) {
    return lines;
  }

  const kept = lines.slice(0, maxLines);
  let last = kept[maxLines - 1];
  while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) {
    last = last.slice(0, -1).trimEnd();
  }
  kept[maxLines - 1] = `${last}…`;
  return kept;
};

const fitFontSize = (ctx, text, { weight, family, start, min, maxWidth }) => {
  let size = start;
  ctx.font = `${weight} ${size}px ${family}`;
  while (size > min && ctx.measureText(text).width > maxWidth) {
    size -= 4;
    ctx.font = `${weight} ${size}px ${family}`;
  }
  return size;
};

const loadImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.decoding = 'async';
  if (/^https?:/i.test(src)) {
    image.crossOrigin = 'anonymous';
  }
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error(`Unable to load image: ${src.slice(0, 60)}`));
  image.src = src;
});

// SVGs are re-sized before rasterising so logos stay crisp at print resolution.
const loadSvgAtSize = async (url, width, height) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Unable to load ${url}`);
  }
  const markup = (await response.text())
    .replace(/<\?xml[^>]*>/, '')
    .replace(/<svg\b/, `<svg width="${Math.round(width)}" height="${Math.round(height)}" preserveAspectRatio="xMidYMid meet"`);
  return loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`);
};

const ensureFonts = async () => {
  if (typeof document === 'undefined' || !document.fonts?.load) {
    return;
  }
  try {
    await Promise.all([
      document.fonts.load(`800 120px ${HEADING_FONT}`),
      document.fonts.load(`700 60px ${BODY_FONT}`),
      document.fonts.load(`600 60px ${BODY_FONT}`),
      document.fonts.load(`800 60px ${BODY_FONT}`),
    ]);
  } catch {
    // Fall back to system fonts if web fonts are unavailable.
  }
};

const isFinderModule = (row, col, size) => {
  const inTop = row < FINDER_SIZE;
  const inLeft = col < FINDER_SIZE;
  const inRight = col >= size - FINDER_SIZE;
  const inBottom = row >= size - FINDER_SIZE;
  return (inTop && inLeft) || (inTop && inRight) || (inBottom && inLeft);
};

const drawFinder = (ctx, x, y, moduleSize, color) => {
  const outer = moduleSize * 7;
  roundRectPath(ctx, x, y, outer, outer, moduleSize * 2);
  ctx.fillStyle = color;
  ctx.fill();
  fillRoundRect(ctx, x + moduleSize, y + moduleSize, moduleSize * 5, moduleSize * 5, moduleSize * 1.4, '#ffffff');
  fillRoundRect(ctx, x + moduleSize * 2, y + moduleSize * 2, moduleSize * 3, moduleSize * 3, moduleSize, color);
};

/**
 * Draws a branded QR code (rounded finder eyes, Virtual Medic symbol in the
 * middle) into a square area. Uses error-correction level H so the centre
 * badge never stops the code from scanning.
 */
export const drawBrandedQr = async (ctx, { text, x, y, size, accentColor, badge = true }) => {
  const qr = QRCode.create(text, { errorCorrectionLevel: 'H' });
  const count = qr.modules.size;
  const moduleSize = size / count;
  const eyeColor = isScannableAccent(accentColor) ? accentColor : QR_INK;

  // From version 7 an alignment pattern sits in the centre, so only badge
  // smaller codes. Join URLs are short enough to stay at version 6 or below.
  const showBadge = badge && qr.version < 7;
  // Keep the centre badge to roughly 20% of the code's width.
  const badgeModules = showBadge ? Math.floor(count * 0.2) | 1 : 0;
  const badgeStart = Math.floor((count - badgeModules) / 2);
  const badgeEnd = badgeStart + badgeModules;
  const inBadge = (row, col) => showBadge
    && row >= badgeStart - 1 && row < badgeEnd + 1
    && col >= badgeStart - 1 && col < badgeEnd + 1;

  ctx.fillStyle = QR_INK;
  for (let row = 0; row < count; row += 1) {
    for (let col = 0; col < count; col += 1) {
      if (!qr.modules.get(row, col) || isFinderModule(row, col, count) || inBadge(row, col)) {
        continue;
      }
      fillRoundRect(
        ctx,
        x + col * moduleSize + moduleSize * 0.06,
        y + row * moduleSize + moduleSize * 0.06,
        moduleSize * 0.88,
        moduleSize * 0.88,
        moduleSize * 0.3,
        QR_INK,
      );
    }
  }

  drawFinder(ctx, x, y, moduleSize, eyeColor);
  drawFinder(ctx, x + (count - FINDER_SIZE) * moduleSize, y, moduleSize, eyeColor);
  drawFinder(ctx, x, y + (count - FINDER_SIZE) * moduleSize, moduleSize, eyeColor);

  if (showBadge) {
    const badgeSize = badgeModules * moduleSize;
    const badgeX = x + badgeStart * moduleSize;
    const badgeY = y + badgeStart * moduleSize;
    fillRoundRect(ctx, badgeX, badgeY, badgeSize, badgeSize, badgeSize * 0.24, '#ffffff');
    ctx.lineWidth = Math.max(2, moduleSize * 0.35);
    roundRectPath(ctx, badgeX, badgeY, badgeSize, badgeSize, badgeSize * 0.24);
    ctx.strokeStyle = 'rgba(18, 61, 103, 0.12)';
    ctx.stroke();
    try {
      const inset = badgeSize * 0.16;
      const symbolWidth = badgeSize - inset * 2;
      const symbolHeight = symbolWidth * (634 / 788);
      const symbol = await loadSvgAtSize(vmSymbolUrl, symbolWidth, symbolHeight);
      ctx.drawImage(symbol, badgeX + inset, badgeY + (badgeSize - symbolHeight) / 2, symbolWidth, symbolHeight);
    } catch {
      // The code still scans without the badge artwork.
    }
  }
};

const drawHero = async (ctx, { universityName, label, includeUniversityBranding, branding }) => {
  const heroHeight = HERO_HEIGHT;
  const gradient = ctx.createLinearGradient(0, 0, POSTER_WIDTH, heroHeight);
  gradient.addColorStop(0, palette.deep);
  gradient.addColorStop(1, palette.mid);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, POSTER_WIDTH, heroHeight);

  // Soft glows for depth.
  [[POSTER_WIDTH - 260, 220, 900, 0.32], [180, heroHeight - 120, 760, 0.22]].forEach(([cx, cy, radius, alpha]) => {
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    glow.addColorStop(0, `rgba(49, 182, 228, ${alpha})`);
    glow.addColorStop(1, 'rgba(49, 182, 228, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, POSTER_WIDTH, heroHeight);
  });

  const accent = includeUniversityBranding && branding?.colors?.primary ? branding.colors.primary : palette.bright;
  ctx.fillStyle = accent;
  ctx.fillRect(0, heroHeight - 28, POSTER_WIDTH, 28);

  // Virtual Medic lockup, top left.
  const logoHeight = 300;
  const logoWidth = logoHeight * (836.46 / 498.01);
  try {
    const lockup = await loadSvgAtSize(marketingLogos.lockupLight, logoWidth, logoHeight);
    ctx.drawImage(lockup, MARGIN_X, 130, logoWidth, logoHeight);
  } catch {
    ctx.fillStyle = '#ffffff';
    ctx.font = `800 110px ${HEADING_FONT}`;
    ctx.textBaseline = 'top';
    ctx.fillText('Virtual Medic', MARGIN_X, 200);
  }

  // Optional university badge, top right.
  if (includeUniversityBranding) {
    const chipHeight = 300;
    const chipMaxWidth = 900;
    const chipRight = POSTER_WIDTH - MARGIN_X;
    let drewLogo = false;
    if (branding?.logoDataUrl) {
      try {
        const logo = await loadImage(branding.logoDataUrl);
        const innerHeight = chipHeight - 90;
        const scale = Math.min(innerHeight / logo.naturalHeight, (chipMaxWidth - 110) / logo.naturalWidth);
        const drawWidth = logo.naturalWidth * scale;
        const drawHeight = logo.naturalHeight * scale;
        const chipWidth = drawWidth + 110;
        fillRoundRect(ctx, chipRight - chipWidth, 130, chipWidth, chipHeight, 56, '#ffffff');
        ctx.drawImage(logo, chipRight - chipWidth + 55, 130 + (chipHeight - drawHeight) / 2, drawWidth, drawHeight);
        drewLogo = true;
      } catch {
        drewLogo = false;
      }
    }
    if (!drewLogo && universityName) {
      ctx.font = `800 64px ${BODY_FONT}`;
      const lines = wrapText(ctx, universityName, chipMaxWidth - 110, 2);
      const chipWidth = Math.max(...lines.map((line) => ctx.measureText(line).width)) + 110;
      fillRoundRect(ctx, chipRight - chipWidth, 130, chipWidth, chipHeight, 56, 'rgba(255, 255, 255, 0.14)');
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const lineHeight = 80;
      const firstY = 130 + chipHeight / 2 - ((lines.length - 1) * lineHeight) / 2;
      lines.forEach((line, index) => ctx.fillText(line, chipRight - chipWidth / 2, firstY + index * lineHeight));
      ctx.textAlign = 'left';
    }
  }

  // Headline.
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = palette.sky;
  ctx.font = `800 58px ${BODY_FONT}`;
  if ('letterSpacing' in ctx) {
    ctx.letterSpacing = '10px';
  }
  ctx.fillText('STUDENT SIGN-UP', MARGIN_X, 640);
  if ('letterSpacing' in ctx) {
    ctx.letterSpacing = '0px';
  }

  // Long university names shrink to stay within two lines above the QR card.
  const headline = universityName ? `Join ${universityName} on Virtual Medic` : 'Join Virtual Medic';
  const maxTextWidth = POSTER_WIDTH - MARGIN_X * 2;
  let headlineSize = 128;
  let headlineLines;
  for (;;) {
    ctx.font = `800 ${headlineSize}px ${HEADING_FONT}`;
    headlineLines = wrapText(ctx, headline, maxTextWidth, 2);
    const overflowed = headlineLines[headlineLines.length - 1].endsWith('…');
    if (!overflowed || headlineSize <= 88) {
      break;
    }
    headlineSize -= 8;
  }
  const headlineLineHeight = Math.round(headlineSize * 1.17);
  ctx.fillStyle = '#ffffff';
  headlineLines.forEach((line, index) => ctx.fillText(line, MARGIN_X, HEADLINE_TOP + index * headlineLineHeight));

  if (label) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.82)';
    ctx.font = `600 64px ${BODY_FONT}`;
    const [labelLine] = wrapText(ctx, label, maxTextWidth, 1);
    ctx.fillText(labelLine, MARGIN_X, HEADLINE_TOP + (headlineLines.length - 1) * headlineLineHeight + 110);
  }

  return { accent };
};

const drawSteps = (ctx, top) => {
  const steps = [
    ['Scan', 'Point your phone camera at the code'],
    ['Sign up', 'Create your account with your email'],
    ['Verify', 'Confirm your email, then sign in'],
  ];
  const columnWidth = (POSTER_WIDTH - MARGIN_X * 2) / steps.length;

  steps.forEach(([title, body], index) => {
    const centerX = MARGIN_X + columnWidth * index + columnWidth / 2;
    ctx.beginPath();
    ctx.arc(centerX, top + 56, 56, 0, Math.PI * 2);
    ctx.fillStyle = palette.mist;
    ctx.fill();
    ctx.fillStyle = palette.mid;
    ctx.font = `800 60px ${HEADING_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(index + 1), centerX, top + 60);

    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = palette.ink;
    ctx.font = `800 54px ${BODY_FONT}`;
    ctx.fillText(title, centerX, top + 190);
    ctx.fillStyle = palette.inkSoft;
    ctx.font = `600 40px ${BODY_FONT}`;
    wrapText(ctx, body, columnWidth - 100, 2).forEach((line, lineIndex) => {
      ctx.fillText(line, centerX, top + 252 + lineIndex * 52);
    });
  });
  ctx.textAlign = 'left';
};

const describeDomains = (domains = []) => {
  if (!domains.length) {
    return '';
  }
  return `Use your ${domains.map((domain) => `@${domain}`).join(' or ')} email`;
};

/**
 * Renders the printable A4 poster for a join link onto a new canvas.
 * Returns the canvas plus the QR/URL areas (for PDF link annotations).
 */
export const renderJoinPoster = async ({
  joinUrl,
  universityName,
  label,
  expiresAt,
  allowedDomains = [],
  includeUniversityBranding = true,
  branding = null,
}) => {
  await ensureFonts();

  const canvas = document.createElement('canvas');
  canvas.width = POSTER_WIDTH;
  canvas.height = POSTER_HEIGHT;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, POSTER_WIDTH, POSTER_HEIGHT);

  const { accent } = await drawHero(ctx, { universityName, label, includeUniversityBranding, branding });

  // QR card overlapping the hero.
  const cardSize = CARD_SIZE;
  const cardX = (POSTER_WIDTH - cardSize) / 2;
  const cardY = CARD_TOP;
  ctx.save();
  ctx.shadowColor = 'rgba(13, 45, 78, 0.28)';
  ctx.shadowBlur = 90;
  ctx.shadowOffsetY = 30;
  fillRoundRect(ctx, cardX, cardY, cardSize, cardSize, 90, '#ffffff');
  ctx.restore();
  ctx.lineWidth = 10;
  roundRectPath(ctx, cardX + 5, cardY + 5, cardSize - 10, cardSize - 10, 86);
  ctx.strokeStyle = accent;
  ctx.globalAlpha = 0.35;
  ctx.stroke();
  ctx.globalAlpha = 1;

  const qrPadding = 120;
  const qrSize = cardSize - qrPadding * 2;
  await drawBrandedQr(ctx, {
    text: joinUrl,
    x: cardX + qrPadding,
    y: cardY + qrPadding,
    size: qrSize,
    accentColor: includeUniversityBranding ? branding?.colors?.primary : palette.mid,
  });

  // URL fallback.
  const urlTop = cardY + cardSize + 140;
  ctx.textAlign = 'center';
  ctx.fillStyle = palette.inkSoft;
  ctx.font = `600 46px ${BODY_FONT}`;
  ctx.fillText('No camera? Visit', POSTER_WIDTH / 2, urlTop);
  const urlText = displayJoinUrl(joinUrl);
  const urlFontSize = fitFontSize(ctx, urlText, {
    weight: 800, family: BODY_FONT, start: 62, min: 36, maxWidth: POSTER_WIDTH - MARGIN_X * 2,
  });
  ctx.fillStyle = palette.mid;
  ctx.fillText(urlText, POSTER_WIDTH / 2, urlTop + urlFontSize + 24);
  const urlWidth = ctx.measureText(urlText).width;
  ctx.textAlign = 'left';

  const footerHeight = FOOTER_HEIGHT;
  drawSteps(ctx, POSTER_HEIGHT - footerHeight - 400);

  // Footer.
  const footerY = POSTER_HEIGHT - footerHeight;
  ctx.fillStyle = palette.mist;
  ctx.fillRect(0, footerY, POSTER_WIDTH, footerHeight);
  ctx.textBaseline = 'middle';
  ctx.fillStyle = palette.ink;
  ctx.font = `700 46px ${BODY_FONT}`;
  const footerNotes = [
    expiresAt ? `Code valid until ${dayjs(expiresAt).format('D MMMM YYYY')}` : '',
    describeDomains(allowedDomains),
  ].filter(Boolean).join('  ·  ');
  ctx.fillText(footerNotes || 'Scan with your phone to get started', MARGIN_X, footerY + footerHeight / 2);
  ctx.textAlign = 'right';
  ctx.fillStyle = palette.mid;
  ctx.font = `800 50px ${HEADING_FONT}`;
  ctx.fillText('virtualmedic.uk', POSTER_WIDTH - MARGIN_X, footerY + footerHeight / 2);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  return {
    canvas,
    regions: {
      qr: { x: cardX, y: cardY, width: cardSize, height: cardSize },
      url: {
        x: (POSTER_WIDTH - urlWidth) / 2,
        y: urlTop + 20,
        width: urlWidth,
        height: urlFontSize + 20,
      },
    },
  };
};

/** Renders just the branded QR code on a white square (for slides). */
export const renderQrOnly = async ({ joinUrl, accentColor, size = 1600 }) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  const padding = Math.round(size * 0.08);
  await drawBrandedQr(ctx, {
    text: joinUrl,
    x: padding,
    y: padding,
    size: size - padding * 2,
    accentColor,
  });
  return canvas;
};

/** Small JPEG copy of a rendered canvas for on-screen previews. */
export const canvasToPreviewDataUrl = (canvas, width = 900) => {
  const scale = Math.min(1, width / canvas.width);
  const preview = document.createElement('canvas');
  preview.width = Math.round(canvas.width * scale);
  preview.height = Math.round(canvas.height * scale);
  const ctx = preview.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, preview.width, preview.height);
  return preview.toDataURL('image/jpeg', 0.9);
};

const triggerDownload = (href, filename) => {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Unable to export image'))), 'image/png');
});

export const downloadCanvasAsPng = async (canvas, filename) => {
  const blob = await canvasToBlob(canvas);
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

export const downloadPosterAsPdf = async ({ canvas, regions, joinUrl, filename, title }) => {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  pdf.setProperties({ title: title || 'Virtual Medic sign-up QR code', creator: 'Virtual Medic' });
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');

  // Make the QR code and the printed URL clickable in the PDF.
  const scaleX = pageWidth / canvas.width;
  const scaleY = pageHeight / canvas.height;
  Object.values(regions || {}).forEach((region) => {
    pdf.link(region.x * scaleX, region.y * scaleY, region.width * scaleX, region.height * scaleY, { url: joinUrl });
  });

  pdf.save(filename);
};

export const buildPosterFilename = (universityName, label, extension, suffix = '') => {
  const slug = [universityName, label]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `virtual-medic-signup-${slug || 'qr'}${suffix ? `-${suffix}` : ''}.${extension}`;
};
